/**
 * Module dependencies.
 */

import React from 'react';
import { cn } from 'app/lib/utils';
import { formatInCurrency } from '../format';
import { useDisplayMoney } from '../use-display-money';

/**
 * EUR for a USD amount at today's rate — or the exact euros when known (e.g.
 * what a repayment actually cost).
 */

export function useToEur(): (usd: number) => number {
  const { usdPerEur } = useDisplayMoney();

  return (usd: number) => (usdPerEur > 0 ? usd / usdPerEur : 0);
}

/**
 * Format a EUR / USD pair as one line ("€150.00 · $168.19").
 */

export function dualText(eur: number, usd: number, sign = ''): string {
  return `${sign}${formatInCurrency(eur, 'EUR')} · ${sign}${formatInCurrency(usd, 'USD')}`;
}

/**
 * Export `DualMoney` — a money cell that always shows both currencies: euros on
 * top, dollars underneath. Loans live in a USD-pegged unit (xUSD) but are paid
 * in euros, so either one alone is misleading. `eur` overrides the conversion
 * when the exact euro amount is known.
 */

export function DualMoney({
  usd,
  eur,
  sign = '',
  align = 'right',
  className
}: {
  usd: number;
  eur?: number | null;
  sign?: string;
  align?: 'left' | 'right';
  className?: string;
}): JSX.Element {
  const toEur = useToEur();
  const euros = eur ?? toEur(usd);

  return (
    <div className={cn('tabular-nums leading-tight', align === 'right' ? 'text-right' : 'text-left', className)}>
      <div>
        {sign}
        {formatInCurrency(euros, 'EUR')}
      </div>
      <div className={'text-[11px] font-normal text-muted-foreground'}>
        {sign}
        {formatInCurrency(usd, 'USD')}
      </div>
    </div>
  );
}
